// @flow
import * as React from 'react';
import { withRouter } from 'react-router';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import type { Match, RouterHistory } from 'react-router-dom';
import { withLeaflet } from 'react-leaflet';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import FiberDamageAction from 'esm-web-pro/containers/pages/network/objects/fiber/FiberDamageAction';
import RowActions from 'esm-web-pro/components/layout/sidePanel/RowActions';
import MeatballsIcon from 'esm-web-pro/components/layout/sidePanel/icons/MeatballsIcon';
import actionMessages from 'esm-web-pro/messages/actionMessages';
import { connectAPI } from 'speed-js-react';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { Map } from 'esm-web-pro/containers/map/types';
import { highlightFiberCircuit } from 'esm-web-pro/containers/pages/network/highlight';
import type { VisualisedFiber } from 'esm-web-pro/containers/pages/network/objects/cable/FiberList';
import type { LeafletContext } from 'react-leaflet/lib/types';
import ReactTooltip from 'react-tooltip';
import { POINT12_CONTENT_TOOLTIP_KEY } from
    'esm-web-pro/containers/pages/network/objects/point12/CableList/Point12CableList';
import ContentRow from 'esm-web-pro/components/layout/sidePanel/ContentRow';
import ValueText from 'esm-web-pro/components/layout/sidePanel/ValueText';
import styled from 'styled-components';
import { Box, Text } from 'grommet';

type FiberType = Object;

type Props = {
    // Passed props
    fiber: FiberType,
    cable: Object,
    damaged: boolean,
    map: Map,
    highlightFiber: Function,
    editor: BaseEditor,
    clicked: string,
    setClicked: Function,
    reload: Function,

    // Received props
    intl: IntlShape,
    leaflet: LeafletContext,
    history: RouterHistory,
    match: Match,
    getFiberCircuitAndPoints: (uuid: string, map: Map) => Promise<Object>,
};


const CableFiber = (props: Props) => {
    const {
        fiber, cable, damaged, map, highlightFiber, editor, clicked, setClicked, reload,
        intl, leaflet, history, match, getFiberCircuitAndPoints,
    } = props;

    const getCircuit = () => {
        if (getFiberCircuitAndPoints) {
            getFiberCircuitAndPoints(fiber.uuid, map).then((vFiber: VisualisedFiber) => {
                editor.unregisterAllLoadingCallback();
                const [callbacks, circuitPath] = highlightFiberCircuit(
                    vFiber.path, vFiber, leaflet.map, highlightFiber,
                );
                editor.registerMultipleLoadingCallbacks(callbacks);
                leaflet.map.highlightedGeometry = {
                    bounds: circuitPath, center: null,
                };
                leaflet.map.panInsideBounds(circuitPath);
                editor.reloadFeatures();
            });
        }
        const rowId = `Fiber ${fiber.id}`;
        setClicked(rowId);
    };

    return (
        <>
            <ContentRow
                padLevel={1}
                onClick={() => {
                    if (!cable.patch) {
                        getCircuit();
                    }
                }}
                clickedRowIdentity={clicked}
                rowId={`Fiber ${fiber.id}`}
                actions={(
                    <>
                        {map.is_draft && (
                            <FiberDamageAction
                                map={map}
                                fiber={fiber}
                                damaged={damaged}
                                ActionComponent={RowActions}
                                actionProps={{
                                    size: '20px',
                                    width: '3px',
                                    tooltip: true,
                                }}
                                updateObject={reload}
                                pad={{ right: 'xxsmall' }}
                            />
                        )}
                        <RowActions
                            actions={[{
                                key: cable.uuid,
                                icon: <MeatballsIcon />,
                                size: '20px',
                                width: '3px',
                                callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const path = match.url.split('/layer');
                                    history.push(`${path[0]}/layer/fiber/${fiber.identity}`);
                                },
                                tooltip: intl.formatMessage(actionMessages.more),
                            }]}
                        />
                    </>
                )}
            >
                <FiberTextContainer>
                    {(fiber.attributes?.color) && (
                        <CableFiberColor
                            color={fiber.attributes?.color}
                            stripeCount={fiber.attributes?.stripe_count}
                            tubeNumber={fiber.attributes?.tube_number}
                        />
                    )}

                    <FormattedMessage id="fiberlist-item-title" defaultMessage="Fiber" />
                    {` ${fiber.sequence_number}`}
                </FiberTextContainer>
            </ContentRow>
            <ReactTooltip
                id={POINT12_CONTENT_TOOLTIP_KEY}
                effect="solid"
                place="top"
                className="react-tooltip"
            />
        </>
    );
};

// Small component to display the fiber color and stripe count
type FiberColorProps = {
    color?: string,
    stripeCount?: number,
    tubeNumber?: number,
};
export const CableFiberColor = ({ color, stripeCount, tubeNumber }: FiberColorProps) => {
    if (color === undefined || stripeCount === undefined || tubeNumber === undefined) {
        return null;
    }

    return (
        <>

            <FiberColorBox color={color} />
            {Array.from(Array(stripeCount).keys()).map((i) => (
                <FiberColorBar color={color} key={i} />
            ))}
            <Text size="small">
                <FormattedMessage id="tube" defaultMessage="Tube" />
                {` ${tubeNumber} `}
            </Text>

        </>
    );
};

CableFiberColor.defaultProps = {
    color: undefined,
    stripeCount: undefined,
    tubeNumber: undefined,
};

export const FiberColorBox = styled(Box)`
    border: 1px solid #6e7482;
    width: 12px;
    height: 12px;
    background-color: ${(props) => props.color};
    border-radius: 3px;
`;


export const FiberColorBar = styled(Box)`
    border-color: #6e7482;
    width: 2px;
    height: 10px;
    background-color: grey;
    border-radius: 1px;
`;

export const FiberTextContainer = styled(ValueText)`
    display: flex;
    align-items: center;
    gap: 3px;
`;


export const fiberCircuitCallback = (apiClient: ApiClient) => ({
    getFiberCircuitAndPoints: {
        send: (uuid: string, map: Map) => {
            if (map.is_draft) {
                return apiClient.resources.fiber.circuit(uuid, { is_draft: map.uuid });
            }
            return apiClient.resources.fiber.circuit(uuid);
        },
    },
});

export default injectIntl(withLeaflet(withRouter(connectAPI(fiberCircuitCallback)(CableFiber))));
